import React, { useMemo } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import PageLayout from '@/components/PageLayout'
import Header from '@/components/Header'
import Back from '@/components/Back'
import { useAllRecordListType } from '@/hooks/listStatus'
import { formatDateTime, formatThousands } from '@/utils'
import dayjs from 'dayjs'

export default React.memo(function RecordDetail() {
  const { t } = useTranslation()
  const location = useLocation()
  const navigate = useNavigate()
  const record = location.state?.record || {}
  const { getAllRecordListType } = useAllRecordListType()

  // 兼容两种数据源：AssetFlowTableListItem（change 为 string）和 ResponsesMemberAllFlowResp（change 为 number）
  const changeNum = typeof record.change === 'string' ? (parseFloat(record.change) || 0) : (record.change || 0)
  const isPositive = changeNum >= 0
  const currencyName = record.type?.toUpperCase() || 'USD'
  const isFiat = ['USD'].includes(currencyName)

  // 格式化金额显示
  const amountDisplay = useMemo(() => {
    const sign = isPositive ? '+' : '-'
    const prefix = isFiat ? '$' : ''
    const suffix = isFiat ? '' : ` ${currencyName}`
    return `${sign}${prefix}${formatThousands(Math.abs(changeNum), 2, true)}${suffix}`
  }, [changeNum, isPositive, isFiat, currencyName])

  // 交易类型文字（使用 useAllRecordListType 枚举）
  const typeLabel = useMemo(() => {
    return getAllRecordListType(record.event || '', record.event || '')
  }, [record.event, getAllRecordListType])

  // 格式化时间（兼容 updated_at 和 created_at）
  const timeDisplay = useMemo(() => {
    return formatDateTime(record.updated_at || record.created_at)
  }, [record.updated_at, record.created_at])

  // 交易前余额
  const beforeBalance = useMemo(() => {
    if (record.before === undefined || record.before === null || record.before === '') return '--'
    const num = typeof record.before === 'string' ? parseFloat(record.before) : record.before
    return isNaN(num) ? '--' : formatThousands(num, 2, true)
  }, [record.before])

  // 交易后余额
  const afterBalance = useMemo(() => {
    if (record.after === undefined || record.after === null || record.after === '') return '--'
    const num = typeof record.after === 'string' ? parseFloat(record.after) : record.after
    return isNaN(num) ? '--' : formatThousands(num, 2, true)
  }, [record.after])

  // 备注
  const remarkDisplay = record.remark || '--'

  return (
    <PageLayout>
      <Header
        leftChildren={<Back />}
        centerChildren={<span className="text-base font-medium text-black">{t('交易详情')}</span>}
        rightChildren={
          <div className="flex items-center justify-center cursor-pointer" onClick={() => navigate('/my/help')}>
            <img src="/icon/question.svg" alt="" className="w-5 h-5" />
          </div>
        }
      />

      <div className="flex flex-col items-center pt-10 pb-6 px-5">
        {/* 图标 */}
        <div className="w-16 h-16 rounded-full bg-[#F2F4F7] flex items-center justify-center mb-6">
          <img src={`/icon/${record.event}.svg`} alt="" className="w-8 h-8" />
        </div>

        {/* 金额 */}
        <div className="text-3xl font-bold text-[#1D2129] mb-3">
          {amountDisplay}
        </div>

        {/* 状态 */}
        <div className="flex items-center space-x-1.5">
          <div className="flex items-center justify-center">
            <img src="/images/card-v2/success-status.svg" alt="" />
          </div>
          <span className="text-sm text-[#1D2129]">{t('已完成')}</span>
        </div>
      </div>

      {/* 详情信息 */}
      <div className="px-5 mt-4">
        {/* 交易类型 */}
        <div className="flex items-center justify-between py-2">
          <span className="text-sm text-[#86909C] mr-2">{t('交易类型')}</span>
          <span className="text-sm text-[#1D2129]">{typeLabel}</span>
        </div>

        {/* 交易币种 */}
        <div className="flex items-center justify-between py-2">
          <span className="text-sm text-[#86909C] mr-2">{t('交易币种')}</span>
          <span className="text-sm text-[#1D2129]">{currencyName}</span>
        </div>

        {/* 交易前余额 */}
        <div className="flex items-center justify-between py-2">
          <span className="text-sm text-[#86909C] mr-2">{t('交易前余额')}</span>
          <span className="text-sm text-[#1D2129]">{beforeBalance}</span>
        </div>

        {/* 交易后余额 */}
        <div className="flex items-center justify-between py-2">
          <span className="text-sm text-[#86909C] mr-2">{t('交易后余额')}</span>
          <span className="text-sm text-[#1D2129]">{afterBalance}</span>
        </div>

        {/* 备注 */}
        <div className="flex items-center justify-between py-2">
          <span className="text-sm text-[#86909C] mr-2">{t('备注')}</span>
          <span className="text-sm text-[#1D2129]">{remarkDisplay}</span>
        </div>

        {/* 交易时间 */}
        <div className="flex items-center justify-between py-2">
          <span className="text-sm text-[#86909C] mr-2">{t('交易时间')}</span>
          <span className="text-sm text-[#1D2129]">{timeDisplay}</span>
        </div>
      </div>
    </PageLayout>
  )
})
